import React from "react";

/**
 * Top-level error boundary.  Catches any runtime React error so we never
 * "black-screen" the entire app again — the user always sees a graceful
 * fallback with a reload button and a diagnostic message they can share.
 *
 * We deliberately keep this dead-simple; heavier error reporting (Sentry
 * etc.) can hook into `componentDidCatch` later without changing the UI.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface to the browser console + any future error tracker.
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  handleReload = () => {
    // Preserve the current path so the user lands back where they were.
    window.location.reload();
  };

  handleHome = () => {
    window.location.href = "/dashboard";
  };

  render() {
    if (!this.state.error) return this.props.children;
    const msg = this.state.error?.message || String(this.state.error);
    return (
      <div className="min-h-screen bg-obsidian-900 text-zinc-100 grid place-items-center p-6">
        <div
          className="max-w-lg w-full obs-card border-blaze/40"
          data-testid="error-boundary"
        >
          <div className="overline text-blaze">Something broke</div>
          <h1 className="font-heading text-3xl uppercase mt-2">Screen crashed</h1>
          <p className="text-sm text-zinc-400 mt-3">
            The app hit an unexpected error while rendering this screen. Your
            data is safe — the crash was isolated to the UI. You can reload
            this page or head back to the dashboard.
          </p>
          <pre className="mt-4 text-[11px] text-zinc-500 bg-obsidian-950/60 border border-obsidian-600 rounded-sm p-3 max-h-40 overflow-auto whitespace-pre-wrap break-words">
            {msg}
          </pre>
          <div className="mt-5 flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={this.handleReload}
              className="blaze-btn px-4 py-2 text-[11px] uppercase tracking-widest"
              data-testid="error-boundary-reload"
            >
              Reload page
            </button>
            <button
              type="button"
              onClick={this.handleHome}
              className="ghost-btn px-4 py-2 text-[11px] uppercase tracking-widest"
              data-testid="error-boundary-home"
            >
              Back to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
}
